import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import GUI from "lil-gui";
import * as CANNON from "cannon-es";

/**
 * Debug
 */
const gui = new GUI();
const debugObject = {};
debugObject.count = 1

debugObject.createSphere = () => {
    for(let i = 0; i < debugObject.count; i++) {
		createSphere(Math.abs(Math.random()), {
			x: (Math.random() - 0.5) * 10,
			y: 3,
			z: (Math.random() - 0.5) * 10,
		});
    }
};

debugObject.createBox = () => {
    for(let i = 0; i < debugObject.count; i++) {
        createBox(
            Math.abs(Math.random()),
            Math.abs(Math.random()),
            Math.abs(Math.random()),
            {
                x: (Math.random() - 0.5) * 10,
                y: 3,
                z: (Math.random() - 0.5) * 10,
            }
        )
    }
}

debugObject.reset = () => {
    for(const object of objectsToUpdate) {
        object.body.removeEventListener('collide', playHitSound)
        world.removeBody(object.body)

        scene.remove(object.mesh)
    }

    objectsToUpdate.splice(0, objectsToUpdate.length)
}

gui.add(debugObject, "createSphere");
gui.add(debugObject, "createBox")
gui.add(debugObject, "reset")
gui.add(debugObject, "count").min(1).max(10).step(1)

/**
 * Base
 */
// Canvas
const canvas = document.querySelector("canvas.webgl");

// Scene
const scene = new THREE.Scene();

/**
 * Sound
 */

const hitSound = new Audio('/physics/hit.mp3')

const playHitSound = (collision) => {

    if(collision.contact.getImpactVelocityAlongNormal() > 1.5) {
        hitSound.currentTime = 0
        hitSound.volume = Math.random()
        hitSound.play()
    }
}

/**
 * Physics
 */
const world = new CANNON.World();
world.broadphase = new CANNON.SAPBroadphase(world)
world.gravity.set(0, -9.81, 0);
world.allowSleep = true

// Material
const defaultMaterial = new CANNON.Material("default");

const defaultConstactMaterial = new CANNON.ContactMaterial(
	defaultMaterial,
	defaultMaterial,
	{
		friction: 0.1,
		restitution: 0.5,
	}
);

world.addContactMaterial(defaultConstactMaterial);
world.defaultContactMaterial = defaultConstactMaterial;

// Floor
const floorShape = new CANNON.Plane();

// Floor
const floorBody = new CANNON.Body({
	shape: floorShape,
	mass: 0,
	position: new CANNON.Vec3(0, 0, 0),
});

floorBody.quaternion.setFromAxisAngle(new CANNON.Vec3(-1, 0, 0), Math.PI * 0.5);

world.addBody(floorBody);

/**
 * Floor
 */
const floor = new THREE.Mesh(
	new THREE.PlaneGeometry(10, 10),
	new THREE.MeshStandardMaterial({
		color: "#777777",
		metalness: 0.3,
		roughness: 0.4,
		envMapIntensity: 0.5,
	})
);
floor.receiveShadow = true;
floor.rotation.x = -Math.PI * 0.5;
scene.add(floor);

/**
 * Lights
 */
const ambientLight = new THREE.AmbientLight(0xffffff, 2.1);
scene.add(ambientLight);

const directionalLight = new THREE.DirectionalLight(0xffffff, 0.6);
directionalLight.castShadow = true;
directionalLight.shadow.mapSize.set(1024, 1024);
directionalLight.shadow.camera.far = 15;
directionalLight.shadow.camera.left = -7;
directionalLight.shadow.camera.top = 7;
directionalLight.shadow.camera.right = 7;
directionalLight.shadow.camera.bottom = -7;
directionalLight.position.set(5, 5, 5);
scene.add(directionalLight);

/**
 * Sizes
 */
const sizes = {
	width: window.innerWidth,
	height: window.innerHeight,
};

window.addEventListener("resize", () => {
	// Update sizes
	sizes.width = window.innerWidth;
	sizes.height = window.innerHeight;

	// Update camera
	camera.aspect = sizes.width / sizes.height;
	camera.updateProjectionMatrix();

	// Update renderer
	renderer.setSize(sizes.width, sizes.height);
	renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
});

/**
 * Camera
 */
// Base camera
const camera = new THREE.PerspectiveCamera(
	75,
	sizes.width / sizes.height,
	0.1,
	100
);
camera.position.set(-5, 5, 5);
scene.add(camera);

// Controls
const controls = new OrbitControls(camera, canvas);
controls.enableDamping = true;

/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
	canvas: canvas,
});
renderer.shadowMap.enabled = true;
renderer.shadowMap.type = THREE.PCFSoftShadowMap;
renderer.setSize(sizes.width, sizes.height);
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

/**
 * Utils
 */

const objectsToUpdate = [];

const sphereGeometry = new THREE.SphereGeometry(1, 20, 20);
const sphereMaterial = new THREE.MeshStandardMaterial({
	metalness: 0.3,
	roughness: 0.4,
});

const createSphere = (radius, position) => {
	// Threejs mesh
	const mesh = new THREE.Mesh(sphereGeometry, sphereMaterial);
	mesh.scale.set(radius, radius, radius);
	mesh.castShadow = true;
	mesh.position.copy(position);
	scene.add(mesh);

	// Cannon body

	const shape = new CANNON.Sphere(radius);
	const body = new CANNON.Body({
		mass: (4 / 3) * Math.PI * Math.pow(radius, 3),
		shape,
		material: defaultMaterial,
	});

	body.position.copy(position);
    body.addEventListener('collide', playHitSound)
	world.addBody(body);

	// Save object
	objectsToUpdate.push({
		mesh,
		body,
	});
};

const boxGeometry = new THREE.BoxGeometry(1, 1, 1);
const boxMaterial = new THREE.MeshStandardMaterial({
	metalness: 0.3,
	roughness: 0.4,
});

const createBox = (x, y, z, position) => {
	const mesh = new THREE.Mesh(boxGeometry, boxMaterial);
	mesh.scale.set(x, y, z);
	mesh.castShadow = true;
	scene.add(mesh);

	const shape = new CANNON.Box(new CANNON.Vec3(x/2, y/2, z/2));
	const body = new CANNON.Body({
		mass: x * y * z,
		shape,
		material: defaultMaterial,
	});

	body.position.copy(position);
    body.addEventListener('collide', playHitSound)
	world.addBody(body);

	objectsToUpdate.push({
		mesh,
		body,
	});
};
/**
 * Animate
 */
const clock = new THREE.Clock();
let oldElapsedTime = 0;

const tick = () => {
	const elapsedTime = clock.getElapsedTime();
	const deltaTime = elapsedTime - oldElapsedTime;
	oldElapsedTime = elapsedTime;

	// Update physics

	world.step(1 / 60, deltaTime, 3);

	for (const object of objectsToUpdate) {
		object.mesh.position.copy(object.body.position);
		object.mesh.quaternion.copy(object.body.quaternion)
	}

	// Update controls
	controls.update();

	// Render
	renderer.render(scene, camera);

	// Call tick again on the next frame
	window.requestAnimationFrame(tick);
};

tick();
